import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal, Spinner } from 'elmo-elements';
import { renderAsync } from 'docx-preview';
import { useDispatch } from 'react-redux';
import { useLazyGetFramesQuery } from '@/modules/services';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { ElearningDataType } from '@/modules/types/elearning';
import { buildElearningDocx } from '@/lib/exportToDocument';
// import 'docx-preview/dist/docx-preview.css';
import './DocxPreviewModal.scss';

export type DocxPreviewModalProps = {
  isOpened: boolean;
  onClose: () => void;
  isBusy: boolean;
  onBusyChange: (busy: boolean) => void;
  elearningData: ElearningDataType;
};

/**
 * Fetches module frames, builds a .docx, and renders it in the browser using docx-preview.
 */
const DocxPreviewModal = ({
  isOpened,
  onClose,
  isBusy,
  onBusyChange,
  elearningData,
}: DocxPreviewModalProps): React.ReactElement => {
  const dispatch = useDispatch();
  const [getFrames] = useLazyGetFramesQuery();
  const containerRef = useRef<HTMLDivElement>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [fetchingDoc, setFetchingDoc] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const elearningDataRef = useRef(elearningData);
  elearningDataRef.current = elearningData;

  useEffect(() => {
    if (!isOpened) {
      setBlob(null);
      setError(null);
      setFetchingDoc(false);
      return undefined;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      setFetchingDoc(true);
      setError(null);
      setBlob(null);
      const edu = elearningDataRef.current;
      try {
        const frames = await getFrames({ elearningId: edu.id });
        if (cancelled) {
          return;
        }
        if (!frames.isSuccess) {
          setError('Could not load module content for preview.');
          return;
        }
        const nextBlob = await buildElearningDocx(frames.data, edu);
        if (!cancelled) {
          setBlob(nextBlob);
        }
      } catch {
        if (!cancelled) {
          setError('Could not build document preview.');
          dispatch({
            type: flashMessageActions.addFlashMessage,
            payload: {
              isActive: true,
              message: 'Preview failed. Please try again.',
            },
          });
        }
      } finally {
        if (!cancelled) {
          setFetchingDoc(false);
        }
      }
    };

    load().catch(() => {
      /* errors surfaced via setError / flash inside load */
    });

    return () => {
      cancelled = true;
    };
  }, [isOpened, elearningData.id, getFrames, dispatch]);

  useLayoutEffect(() => {
    if (!isOpened || !blob) {
      return undefined;
    }

    const el = containerRef.current;
    if (!el) {
      return undefined;
    }

    let cancelled = false;

    const run = async (): Promise<void> => {
      setRendering(true);
      setError(null);
      el.innerHTML = '';
      try {
        await renderAsync(blob, el, undefined, {
          className: 'docx-preview',
          inWrapper: true,
        });
      } catch {
        if (!cancelled) {
          setError('Could not render document preview.');
        }
      } finally {
        if (!cancelled) {
          setRendering(false);
        }
      }
    };

    run().catch(() => {
      /* errors surfaced via setError inside run */
    });

    return () => {
      cancelled = true;
      el.innerHTML = '';
    };
  }, [isOpened, blob]);

  const workBusy = fetchingDoc || rendering;

  useEffect(() => {
    onBusyChange(workBusy);
  }, [workBusy, onBusyChange]);

  const modalBusy = workBusy || isBusy;

  return (
    <Modal
      id="DocxPreviewModal"
      type="large"
      isOpened={isOpened}
      closeModal={() => {
        if (!modalBusy) {
          onClose();
        }
      }}
      title="Document preview"
      closeLabel="Close"
    >
      <p className="docx-preview-modal__hint">
        Preview is approximate; download the file to see the document in Word.
      </p>
      {modalBusy && (
        <div className="docx-preview-modal__loading">
          <Spinner size="xl" ariaLabel="Loading preview" />
        </div>
      )}
      {error && <p role="alert">{error}</p>}
      <div ref={containerRef} className="docx-preview-modal__viewport" />
    </Modal>
  );
};

export default DocxPreviewModal;
